/**
 * quizCenterTrail — where every Quiz Center page sits, as the trail from the top
 * of the module down to it. PageHeader draws the trail as breadcrumbs and sends
 * its back button to the step above the page, so "back" always goes UP the
 * hierarchy: the same place whether the page was opened from the Quiz Center, a
 * bookmark or a link someone shared — never to whatever the browser showed last.
 *
 *   Quiz Center
 *   ├─ Question bank › category › subtopic › New / Edit question
 *   │  ├─ Passages › passage › New / Edit question
 *   │  └─ Upload questions
 *   ├─ Assessments › assessment › Edit · Preview
 *   │  ├─ New assessment
 *   │  └─ Marketplace
 *   ├─ Test series › series › Edit
 *   │  ├─ New series
 *   │  └─ Import
 *   ├─ Analytics
 *   └─ Bulk operations
 *
 * The platform library (`/sa/…`) has the bank and assessments but no centre,
 * and no page for one assessment: its trails start at the super-admin dashboard.
 *
 * A trail is `[{ label, to, icon? }, …, { label }]` — the last step is the page
 * itself. Nothing here touches React, the router or the API.
 */
import { Brain, LayoutDashboard } from "lucide-react";

export const isLibraryPath = (pathname = "") => pathname.startsWith("/sa/");
export const bankBase = (isLibrary) => (isLibrary ? "/sa/marketplace/quiz-questions" : "/quiz-bank");
export const assessmentsBase = (isLibrary) => (isLibrary ? "/sa/marketplace/assessments" : "/assessments");

const CENTER = { label: "Quiz Center", to: "/quiz-center", icon: Brain };
const LIBRARY = { label: "Dashboard", to: "/sa", icon: LayoutDashboard };
const rootOf = (isLibrary) => (isLibrary ? LIBRARY : CENTER);

/** Where the back button goes: the step above the page. */
export const parentOf = (trail) => trail[trail.length - 2]?.to || null;

const idOf = (v) => String(v?._id || v || "");

/** A folder of the bank as a link: the bank opens on it. */
export function folderPath(base, category, subcategory) {
  if (!idOf(category)) return base;
  const qs = new URLSearchParams({ category: idOf(category) });
  if (idOf(subcategory)) qs.set("subcategory", idOf(subcategory));
  return `${base}?${qs}`;
}

// A folder whose name has not loaded yet still gets its step, so the back
// button already points at it.
const nameOf = (categories, id, fallback) =>
  (categories || []).find((c) => idOf(c) === idOf(id))?.name || fallback;

// ── Question bank ────────────────────────────────────────────────────

/** The bank, opened on a folder when one is given. */
export function bankTrail({ isLibrary = false, categories = [], category = "", subcategory = "" } = {}) {
  const base = bankBase(isLibrary);
  const trail = [rootOf(isLibrary), { label: "Question bank", to: base }];
  if (idOf(category)) {
    trail.push({ label: nameOf(categories, category, "Category"), to: folderPath(base, category) });
    if (idOf(subcategory)) {
      trail.push({ label: nameOf(categories, subcategory, "Subtopic"), to: folderPath(base, category, subcategory) });
    }
  }
  return trail;
}

export const uploadTrail = ({ isLibrary = false } = {}) => [...bankTrail({ isLibrary }), { label: "Upload questions" }];

export const passagesTrail = ({ isLibrary = false } = {}) => [
  ...bankTrail({ isLibrary }),
  { label: "Passages", to: `${bankBase(isLibrary)}/passages` },
];

/** One passage — "New passage" until it is saved. */
export const passageTrail = ({ isLibrary = false, passage = null } = {}) => [
  ...passagesTrail({ isLibrary }),
  passage?._id
    ? { label: passage.title || "Passage", to: `${bankBase(isLibrary)}/passages/${passage._id}` }
    : { label: "New passage" },
];

/**
 * The question editor, under the passage it was opened from or else under the
 * folder the question lives in. Pass where it lives NOW (the saved question, or
 * the folder it was started from), not the topic being picked in the form — back
 * returns to where the author came from, not to a folder it is not in yet.
 */
export function questionTrail({ isLibrary = false, isEdit = false, categories, category, subcategory, passage } = {}) {
  const above = passage?._id
    ? passageTrail({ isLibrary, passage })
    : bankTrail({ isLibrary, categories, category, subcategory });
  return [...above, { label: isEdit ? "Edit question" : "New question" }];
}

// ── Assessments ──────────────────────────────────────────────────────

export const assessmentsTrail = ({ isLibrary = false } = {}) => [
  rootOf(isLibrary),
  { label: "Assessments", to: assessmentsBase(isLibrary) },
];

export const assessmentMarketplaceTrail = () => [...assessmentsTrail(), { label: "Marketplace" }];

/** One assessment's own page. */
export const assessmentTrail = ({ id, title } = {}) => [
  ...assessmentsTrail(),
  { label: title || "Assessment", to: `/assessments/${id}` },
];

/** The builder: an edit sits under the assessment's page (the library has none). */
export function assessmentBuilderTrail({ isLibrary = false, id, title } = {}) {
  if (!id) return [...assessmentsTrail({ isLibrary }), { label: "New assessment" }];
  if (isLibrary) return [...assessmentsTrail({ isLibrary }), { label: "Edit assessment" }];
  return [...assessmentTrail({ id, title }), { label: "Edit" }];
}

export const assessmentPreviewTrail = ({ isLibrary = false, id, title } = {}) => [
  ...(isLibrary ? assessmentsTrail({ isLibrary }) : assessmentTrail({ id, title })),
  { label: "Preview" },
];

export const analyticsTrail = () => [CENTER, { label: "Analytics" }];
export const bulkOperationsTrail = () => [CENTER, { label: "Bulk operations" }];

// ── Test series ──────────────────────────────────────────────────────

export const seriesListTrail = () => [CENTER, { label: "Test series", to: "/test-series" }];

/** One series' own page. */
export const seriesTrail = ({ id, title } = {}) => [
  ...seriesListTrail(),
  { label: title || "Test series", to: `/test-series/${id}` },
];

/** Create or edit a series: an edit sits under the series' page. */
export const seriesFormTrail = ({ id, title } = {}) => (id
  ? [...seriesTrail({ id, title }), { label: "Edit" }]
  : [...seriesListTrail(), { label: "New series" }]);

export const seriesImportTrail = () => [...seriesListTrail(), { label: "Import" }];
